import { omitPathParameters, replacePathParameters } from "../../utils.ts";
import type { AxiosInstance, AxiosRequestConfig } from "axios";
import type * as Paths from "./paths";
import type {
  OperationResponse,
  Parameters,
  UnknownParamsObject,
} from "../../common.ts";

interface OperationMethods {
  "getList"(
    parameters?: Parameters<Paths.GetList.QueryParameters> | null,
    data?: any,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.GetList.Responses.$200>;

  "createRecord"(
    parameters?: Parameters<UnknownParamsObject> | null,
    data?: Paths.CreateRecord.RequestBody,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.CreateRecord.Responses.$200>;

  "getRecord"(
    parameters?: Parameters<Paths.GetRecord.PathParameters> | null,
    data?: any,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.GetRecord.Responses.$200>;

  "updateRecord"(
    parameters?: Parameters<Paths.UpdateRecord.PathParameters> | null,
    data?: Paths.UpdateRecord.RequestBody,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.UpdateRecord.Responses.$200>;

  "deleteRecord"(
    parameters?: Parameters<Paths.DeleteRecord.PathParameters> | null,
    data?: any,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.DeleteRecord.Responses.$204>;

  "getCustomerNewForm"(
    parameters?: null,
    data?: null,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.CreateRecord.GetCustomerNewForm.Responses.$200>;
}

export const getCustomerOperations = (client: AxiosInstance) => {
  const getList: OperationMethods["getList"] = (
    parameters?: Paths.GetList.QueryParameters,
    data?: any,
    config?: AxiosRequestConfig,
  ) => {
    const url = "/customer";
    return client.get<Paths.GetList.Responses.$200>(
      replacePathParameters(url, parameters),
      {
        params: omitPathParameters(parameters, url),
        data: data,
        ...config,
      },
    );
  };
  const createRecord: OperationMethods["createRecord"] = (
    parameters: any,
    data: Paths.CreateRecord.RequestBody,
    config?: AxiosRequestConfig,
  ) => {
    const url = "/customer";
    return client.post<Paths.CreateRecord.Responses.$200>(
      replacePathParameters(url, parameters),
      data,
      {
        params: omitPathParameters(parameters, url),
        ...config,
      },
    );
  };
  const getRecord: OperationMethods["getRecord"] = (
    parameters: Paths.GetRecord.QueryParameters &
      Paths.GetRecord.PathParameters,
    data?: any,
    config?: AxiosRequestConfig,
  ) => {
    const url = "/customer/{id}";
    return client.get<Paths.GetRecord.Responses.$200>(
      replacePathParameters(url, parameters),
      {
        params: omitPathParameters(parameters, url),
        data: data,
        ...config,
      },
    );
  };
  const updateRecord: OperationMethods["updateRecord"] = (
    parameters: any,
    data?: Paths.UpdateRecord.RequestBody,
    config?: AxiosRequestConfig,
  ) => {
    const url = "/customer/{id}";
    return client.put<Paths.UpdateRecord.Responses.$200>(
      replacePathParameters(url, parameters),
      data,
      {
        params: omitPathParameters(parameters, url),
        ...config,
      },
    );
  };
  const deleteRecord: OperationMethods["deleteRecord"] = (
    parameters: Paths.DeleteRecord.PathParameters,
    data?: any,
    config?: AxiosRequestConfig,
  ) => {
    const url = "/customer/{id}";
    return client.delete(replacePathParameters(url, parameters), {
      params: omitPathParameters(parameters, url),
      data: data,
      ...config,
    });
  };
  const getCustomerNewForm: OperationMethods["getCustomerNewForm"] = (
    parameters,
    _,
    config,
  ) => {
    const url = "customer/new-form";
    return client.get(replacePathParameters(url, parameters), {
      params: omitPathParameters(parameters, url),
      ...config,
    });
  };

  return {
    getList,
    createRecord,
    getRecord,
    updateRecord,
    deleteRecord,
    getCustomerNewForm,
  };
};
